/**
 * CommissionPayoutDetailPage
 *
 * View a single payout batch with its member statements. Actions available
 * depend on the batch's current status.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { useClinic } from '@/contexts/ClinicContext'
import { useBreadcrumbLabel } from '@/components/ui/breadcrumb'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { ArrowLeft, Check, Download, Loader2, X } from 'lucide-react'
import {
  getPayout,
  approvePayout,
  settlePayout,
  voidPayout,
  exportPayout,
  centsToDollars,
  PayoutBatchStatus,
} from '@/services/commissionService'

const STATUS_COLORS: Record<
  PayoutBatchStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  draft: 'secondary',
  approved: 'outline',
  exported: 'outline',
  settled: 'default',
  voided: 'destructive',
}

const STATUS_LABELS: Record<PayoutBatchStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  exported: 'Exported',
  settled: 'Settled',
  voided: 'Voided',
}

const toTitleCase = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

const formatUSDate = (iso?: string | null) => {
  if (!iso) return ''
  try {
    return format(parseISO(iso), 'MM/dd/yyyy')
  } catch {
    return iso
  }
}

const ROLE_LABELS: Record<string, string> = {
  practitioner: 'Practitioner',
  clinic_admin: 'Clinic Admin',
  admin: 'Admin',
  super_admin: 'System Admin',
}

export function CommissionPayoutDetailPage() {
  const { batchId } = useParams<{ batchId: string }>()
  const { clinic, isAdmin } = useClinic()
  const clinicId = clinic?.id
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { setLabel: setBreadcrumbLabel } = useBreadcrumbLabel()

  const [showSettle, setShowSettle] = useState(false)
  const [showVoid, setShowVoid] = useState(false)
  const [showApprove, setShowApprove] = useState(false)
  const [settleForm, setSettleForm] = useState({
    reference: '',
    method: 'check',
    notes: '',
  })
  const [voidReason, setVoidReason] = useState('')

  const { data: batch, isLoading } = useQuery({
    queryKey: ['commission-payout', clinicId, batchId],
    queryFn: () => getPayout(clinicId!, batchId!),
    enabled: !!clinicId && !!batchId,
  })

  useEffect(() => {
    if (!batch) {
      setBreadcrumbLabel('Payout')
      return
    }
    const name =
      batch.referrer?.name ||
      (batch.referrerUser
        ? `${batch.referrerUser.first_name} ${batch.referrerUser.last_name}`
        : null)
    setBreadcrumbLabel(name ? `${name} — ${formatUSDate(batch.period_start)}` : 'Payout')
    return () => setBreadcrumbLabel(null)
  }, [batch, setBreadcrumbLabel])

  const approveMutation = useMutation({
    mutationFn: () => approvePayout(clinicId!, batchId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-payout'] })
      queryClient.invalidateQueries({ queryKey: ['commission-payouts'] })
      setShowApprove(false)
      toast({ title: 'Payout approved' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      })
    },
  })

  const settleMutation = useMutation({
    mutationFn: () =>
      settlePayout(clinicId!, batchId!, {
        reference: settleForm.reference || undefined,
        method: settleForm.method || undefined,
        notes: settleForm.notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-payout'] })
      queryClient.invalidateQueries({ queryKey: ['commission-payouts'] })
      setShowSettle(false)
      toast({ title: 'Payout marked as settled' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      })
    },
  })

  const voidMutation = useMutation({
    mutationFn: () => voidPayout(clinicId!, batchId!, voidReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-payout'] })
      queryClient.invalidateQueries({ queryKey: ['commission-payouts'] })
      queryClient.invalidateQueries({
        queryKey: ['payout-eligible-statements'],
      })
      queryClient.invalidateQueries({
        queryKey: ['commission-statements'],
      })
      setShowVoid(false)
      setVoidReason('')
      toast({ title: 'Payout voided' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message,
      })
    },
  })

  const handleExport = async () => {
    try {
      const blob = await exportPayout(clinicId!, batchId!)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payout-batch-${batchId}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      queryClient.invalidateQueries({ queryKey: ['commission-payout'] })
      queryClient.invalidateQueries({ queryKey: ['commission-payouts'] })
      toast({ title: 'CSV downloaded' })
    } catch {
      toast({
        variant: 'destructive',
        title: 'Export failed',
      })
    }
  }

  if (!clinicId) return <div className="p-6">No clinic selected.</div>
  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            You need clinic admin access to view payouts.
          </CardContent>
        </Card>
      </div>
    )
  }
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }
  if (!batch) {
    return <div className="p-6">Batch not found.</div>
  }

  const referrerName =
    batch.referrer?.name ||
    (batch.referrerUser
      ? `${batch.referrerUser.first_name} ${batch.referrerUser.last_name}`
      : batch.referrer_user_id.slice(0, 8))

  const canApprove = batch.status === 'draft'
  const canExport = batch.status === 'approved' || batch.status === 'exported'
  const canSettle = batch.status === 'approved' || batch.status === 'exported'
  const canVoid = ['draft', 'approved', 'exported'].includes(batch.status)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost">
          <Link to="/commissions/payouts">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Payouts
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          {canApprove && (
            <Button
              onClick={() => setShowApprove(true)}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              <Check className="h-4 w-4 mr-2" /> Approve
            </Button>
          )}
          {canExport && (
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          )}
          {canSettle && (
            <Button onClick={() => setShowSettle(true)}>Mark Settled</Button>
          )}
          {canVoid && (
            <Button
              variant="destructive"
              onClick={() => setShowVoid(true)}
            >
              <X className="h-4 w-4 mr-2" /> Void
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Payout Batch</CardTitle>
              <CardDescription>
                {referrerName} — {formatUSDate(batch.period_start)} – {formatUSDate(batch.period_end)}
              </CardDescription>
            </div>
            <Badge variant={STATUS_COLORS[batch.status]}>
              {STATUS_LABELS[batch.status]}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Role</p>
              <p className="font-medium capitalize">
                {(batch.role_snapshot &&
                  (ROLE_LABELS[batch.role_snapshot] ||
                    batch.role_snapshot.replace(/_/g, ' '))) ||
                  '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Statements</p>
              <p className="font-medium">{batch.statement_count}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="font-medium text-green-600">
                ${batch.totalDollars || centsToDollars(batch.total_amount_cents)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Reference</p>
              <p className="font-medium">{batch.external_reference || '—'}</p>
              {batch.settlement_method && (
                <p className="text-xs text-muted-foreground capitalize">
                  via {batch.settlement_method}
                </p>
              )}
            </div>
          </div>

          {batch.notes && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">Notes</p>
              <p className="text-sm whitespace-pre-wrap">{batch.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Included Statements</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>Referrals</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(batch.statements || []).length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground"
                  >
                    No statements attached.
                  </TableCell>
                </TableRow>
              )}
              {batch.statements?.map(s => (
                <TableRow key={s.id}>
                  <TableCell>
                    {formatUSDate(s.period_start)} – {formatUSDate(s.period_end)}
                  </TableCell>
                  <TableCell>{s.referral_count}</TableCell>
                  <TableCell>
                    ${centsToDollars(s.eligible_revenue_cents)}
                  </TableCell>
                  <TableCell className="font-medium">
                    ${centsToDollars(s.commission_amount_cents)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{toTitleCase(s.status)}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approve dialog */}
      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Payout?</DialogTitle>
            <DialogDescription>
              Approving locks this batch and makes its statements ready for
              export and settlement. This can&apos;t be undone without voiding
              the batch.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total</span>
              <span className="font-medium text-green-600">
                ${batch.totalDollars || centsToDollars(batch.total_amount_cents)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Statements</span>
              <span className="font-medium">{batch.statement_count}</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowApprove(false)}
              disabled={approveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              <Check className="h-4 w-4 mr-2" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle dialog */}
      <Dialog open={showSettle} onOpenChange={setShowSettle}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Settled</DialogTitle>
            <DialogDescription>
              Record the details of the offline payment so the audit log
              reflects the settlement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Method</Label>
                <Select
                  value={settleForm.method}
                  onValueChange={v =>
                    setSettleForm({ ...settleForm, method: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="ach">ACH / Wire</SelectItem>
                    <SelectItem value="payroll">Payroll</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input
                  value={settleForm.reference}
                  onChange={e =>
                    setSettleForm({
                      ...settleForm,
                      reference: e.target.value,
                    })
                  }
                  placeholder="Check no., wire ref, payroll run"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={settleForm.notes}
                onChange={e =>
                  setSettleForm({ ...settleForm, notes: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettle(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => settleMutation.mutate()}
              disabled={settleMutation.isPending}
            >
              {settleMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Mark Settled
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void dialog */}
      <Dialog open={showVoid} onOpenChange={setShowVoid}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Batch</DialogTitle>
            <DialogDescription>
              Voiding releases the included statements back to the unpaid
              pool. Provide a reason for the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label>Reason</Label>
            <Textarea
              value={voidReason}
              onChange={e => setVoidReason(e.target.value)}
              placeholder="Why is this being voided?"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoid(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => voidMutation.mutate()}
              disabled={!voidReason || voidMutation.isPending}
            >
              {voidMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Void
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* If batch was settled, show navigate back hint after a while */}
      {batch.status === 'settled' && (
        <p className="text-center text-sm text-muted-foreground">
          Settled on{' '}
          {batch.settled_at
            ? new Date(batch.settled_at).toLocaleString()
            : '—'}{' '}
          by{' '}
          <Button
            variant="link"
            className="p-0 h-auto"
            onClick={() => navigate('/commissions/payouts')}
          >
            back to list
          </Button>
        </p>
      )}
    </div>
  )
}

export default CommissionPayoutDetailPage
